import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import type { InputProps } from '@gik/ui/Input';
import { Input } from '@gik/ui/Input';
import React from 'react';

export type TripleInputValue = {
  value1: string;
  value2: string;
  value3: string;
};

export interface ITripleSplitInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  label1: string;
  label2: string;
  label3: string;
  input1Append?: string;
  input2Append?: string;
  input3Append?: string;
  input1Prepend?: string;
  input2Prepend?: string;
  input3Prepend?: string;
  value: TripleInputValue;
  onChange: (value: TripleInputValue) => void;
}

const blockName = 'triple-split-input';

export function TripleSplitInput({
  label1,
  label2,
  label3,
  value: { value1, value2, value3 } = { value1: undefined, value2: undefined, value3: undefined },
  onChange = noop,
  ...inputProps
}: ITripleSplitInputProps) {
  const bem = useBemCN(blockName);

  const onValue1Change = React.useCallback(
    (value: string) => {
      onChange({ value1: value, value2, value3 });
    },
    [onChange, value2, value3]
  );

  const onValue2Change = React.useCallback(
    (value: string) => {
      onChange({ value1, value2: value, value3 });
    },
    [onChange, value1, value3]
  );

  const onValue3Change = React.useCallback(
    (value: string) => {
      onChange({ value1, value2, value3: value });
    },
    [onChange, value1, value2]
  );

  return (
    <div {...bem()}>
      <div>
        <Input {...inputProps} value={value1} onValueChange={onValue1Change} />
        <div>{label1}</div>
      </div>

      <div>
        <Input {...inputProps} value={value2} onValueChange={onValue2Change} />
        <div>{label2}</div>
      </div>

      <div>
        <Input {...inputProps} value={value3} onValueChange={onValue3Change} />
        <div>{label3}</div>
      </div>
    </div>
  );
}
